import TodayIcon from "@web/public/to-day.svg";
import Head from "next/head";
import Link from "next/link";
import type { FC } from "react";
import { type ReactNode, useState } from "react";

const HERO_VIDEO = "https://framerusercontent.com/assets/aLpBsRMsFTNngGOEXOWA5gOlyzs.mp4";

const testimonials = [
  {
    name: "Tyler R.",
    role: "Student",
    text: (
      <>
        I love that it&apos;s centred on &ldquo;today&rdquo;. <strong>I just end up getting more stuff done</strong>,
        and feeling less stressed.
      </>
    ),
  },
  {
    name: "Lyra M.",
    role: "Software Engineer",
    text: (
      <>
        ...I can&apos;t live without it now, <strong>it&apos;s the only thing keeping me sane</strong> at work.
      </>
    ),
  },
  {
    name: "Wanz T.",
    role: "My wife",
    text: (
      <>
        The app&apos;s okay I guess. I only use it because my husband built it.{" "}
        <strong>It&apos;s free though, so why not.</strong>
      </>
    ),
  },
];

const features: {
  title: string;
  description: string;
  cta: string;
  href: string;
  video: string;
  reverse: boolean;
}[] = [
  {
    title: "Simple and Fast.",
    description: "Brain dumping has never been this easy.",
    cta: "Get Started Free",
    href: "/login",
    video: "https://framerusercontent.com/assets/YFPaQLnjp2BR71ELKYBySQc.mp4",
    reverse: false,
  },
  {
    title: "Satisfying.",
    description: "Get shit done, period.",
    cta: "Read More",
    href: "#faq",
    video: "https://framerusercontent.com/assets/atX2n5lgxBKdQmCGa3zWThjWPWU.mp4",
    reverse: true,
  },
  {
    title: "Be Focused.",
    description: "Built for the scattered mind.",
    cta: "I Need Help",
    href: "/login",
    video: "https://framerusercontent.com/assets/JW5BXXHPomT4OYYRRWlL1ZsV6aQ.mp4",
    reverse: false,
  },
];

const faqs = [
  {
    question: "What is To-day?",
    answer:
      "To-day is a todo application that's built to be as fuss free as possible, while providing important features like due dates, categorisation, pomodoro timers and more.",
  },
  {
    question: "What differentiates it from other apps?",
    answer:
      "To-day is streamlined to feel like pen and paper - but supercharged with shortcuts, with everything easy to access. With To-day, you spend as little time as possible on the app, and as much time getting actual work done.",
  },
  {
    question: "Which platform is To-day available on?",
    answer: "To-day is now available on Web, iOS and Android.",
  },
  {
    question: "How much does it cost?",
    answer:
      "To-day is free. You can have up to 40 todos and 3 spaces (just so it is manageable from a maintenance perspective).",
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{ borderBottom: "1px solid #e5e5e5" }}>
      <button
        style={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 0",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "Inter, sans-serif",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span style={{ fontWeight: 500, fontSize: 16, color: "#000" }}>{question}</span>
        <span
          style={{
            fontSize: 24,
            color: "#999",
            transition: "transform 0.2s",
            transform: isOpen ? "rotate(45deg)" : "none",
            marginLeft: 16,
            flexShrink: 0,
          }}
        >
          +
        </span>
      </button>
      <div
        style={{
          overflow: "hidden",
          transition: "max-height 0.3s ease, opacity 0.3s ease",
          maxHeight: isOpen ? 300 : 0,
          opacity: isOpen ? 1 : 0,
        }}
      >
        <p
          style={{
            paddingBottom: 20,
            fontSize: 14,
            lineHeight: 1.7,
            color: "#666",
            fontFamily: "Inter, sans-serif",
            margin: 0,
          }}
        >
          {answer}
        </p>
      </div>
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  // Simple avatar placeholder with initials
  return (
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        backgroundColor: "#f0f0f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 16,
        fontWeight: 600,
        color: "#999",
        flexShrink: 0,
      }}
    >
      {name.charAt(0)}
    </div>
  );
}

const LandingPage: FC = () => {
  return (
    <>
      <Head>
        <title>To-day — A free task manager that&apos;s clean, smooth, and lightweight</title>
        <meta
          name="description"
          content="To-day is a free, lightweight task manager built for people who want to stay focused. Available on Web, iOS, and Android."
        />
      </Head>
      <style jsx global>{`
        html,
        body {
          background-color: #fff !important;
          font-family: "Inter", sans-serif !important;
          color: #000 !important;
        }
        #__next {
          height: auto !important;
          display: block !important;
        }
        @media (max-width: 768px) {
          .landing-testimonials {
            grid-template-columns: 1fr !important;
          }
          .landing-feature {
            flex-direction: column !important;
            gap: 32px !important;
            padding-top: 48px !important;
            padding-bottom: 48px !important;
          }
          .landing-feature-title {
            font-size: 32px !important;
          }
          .landing-feature-desc {
            font-size: 17px !important;
          }
          .landing-feature-text {
            width: 100% !important;
            flex: none !important;
          }
          .landing-feature-cta {
            display: flex !important;
            justify-content: flex-end !important;
          }
        }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#fff",
          color: "#000",
          fontFamily: "Inter, sans-serif",
        }}
      >
        {/* Nav */}
        <nav
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            margin: "0",
            borderBottom: "1px solid #e8e8e8",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TodayIcon style={{ width: 24, height: 24 }} />
            <span style={{ fontWeight: 700, fontSize: 18 }}>To-day</span>
          </div>
          <Link
            href="/login"
            style={{
              backgroundColor: "#000",
              color: "#fff",
              padding: "10px 16px",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              textDecoration: "none",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            Sign In
          </Link>
        </nav>

        {/* Hero */}
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            padding: "80px 24px 40px",
          }}
        >
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 700,
              fontSize: "clamp(48px, 7vw, 80px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            Introducing To-day.
          </div>
          <p
            style={{
              marginTop: 24,
              fontSize: "clamp(16px, 2vw, 20px)",
              color: "#666",
              maxWidth: 460,
              lineHeight: 1.5,
            }}
          >
            A free task manager that&apos;s clean, smooth and lightweight.
          </p>
          <Link
            href="/login"
            style={{
              marginTop: 28,
              backgroundColor: "#000",
              color: "#fff",
              padding: "12px 28px",
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 600,
              textDecoration: "none",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            Get Started
          </Link>
        </section>

        {/* Hero Video */}
        <section style={{ padding: "40px 24px 80px", maxWidth: 900, margin: "0 auto" }}>
          <video
            src={HERO_VIDEO}
            loop
            autoPlay
            muted
            playsInline
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              objectFit: "cover",
            }}
          />
        </section>

        {/* Testimonials */}
        <section style={{ padding: "60px 24px", maxWidth: 1100, margin: "0 auto" }}>
          <div
            className="landing-testimonials"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 20,
            }}
          >
            {testimonials.map((t) => (
              <div
                key={t.name}
                style={{
                  border: "1px solid #e8e8e8",
                  borderRadius: 16,
                  padding: 28,
                  backgroundColor: "#fff",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                  <Avatar name={t.name} />
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#000" }}>{t.name}</div>
                    <div style={{ fontSize: 13, color: "#999" }}>{t.role}</div>
                  </div>
                </div>
                <p style={{ fontSize: 15, lineHeight: 1.6, color: "#444", margin: 0 }}>{t.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <div style={{ backgroundColor: "rgb(247, 247, 247)" }}>
          {features.map((f) => (
            <section
              key={f.title}
              className="landing-feature"
              style={{
                padding: "80px 24px",
                maxWidth: 1100,
                margin: "0 auto",
                display: "flex",
                flexDirection: f.reverse ? "row-reverse" : "row",
                alignItems: "center",
                gap: 60,
              }}
            >
              {/* Video */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <video
                  src={f.video}
                  loop
                  autoPlay
                  muted
                  playsInline
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    display: "block",
                    objectFit: "cover",
                  }}
                />
              </div>
              {/* Text */}
              <div className="landing-feature-text" style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="landing-feature-title"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 46,
                    fontWeight: 700,
                    lineHeight: 1.1,
                    letterSpacing: "-0.02em",
                    color: "#000",
                  }}
                >
                  {f.title}
                </div>
                <p className="landing-feature-desc" style={{ fontSize: 22, color: "#888", lineHeight: 1.4, margin: "16px 0 0" }}>{f.description}</p>
                <div className="landing-feature-cta" style={{ marginTop: 24 }}>
                  <Link
                    href={f.href}
                    style={{
                      display: "inline-block",
                      backgroundColor: "#222222",
                      color: "#fff",
                      padding: "10px 16px",
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      textDecoration: "none",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {f.cta}
                  </Link>
                </div>
              </div>
            </section>
          ))}
        </div>

        {/* FAQ */}
        <section id="faq" style={{ padding: "80px 24px", maxWidth: 720, margin: "0 auto" }}>
          {faqs.map((faq) => (
            <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
          ))}
        </section>

        {/* Bottom CTA */}
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            padding: "60px 24px 80px",
          }}
        >
          <div style={{ fontSize: 32, fontWeight: 700, color: "#000", marginBottom: 24 }}>Sign up today</div>
          <Link
            href="/login"
            style={{
              backgroundColor: "#000",
              color: "#fff",
              padding: "12px 32px",
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              textDecoration: "none",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            Get Started
          </Link>
        </section>

        {/* Footer */}
        <footer
          style={{
            borderTop: "1px solid #e8e8e8",
            padding: "32px 40px",
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <TodayIcon style={{ width: 20, height: 20 }} />
              <span style={{ fontSize: 14, color: "#999" }}>&copy; {new Date().getFullYear()} To-day</span>
            </div>
            <div style={{ display: "flex", gap: 24, fontSize: 14 }}>
              <Link href="/privacy" style={{ color: "#999", textDecoration: "none" }}>
                Privacy Policy
              </Link>
              <Link href="/terms" style={{ color: "#999", textDecoration: "none" }}>
                Terms of Use
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
